import "../../styles/common.css";
import CloseIcon from "../../assets/common/close-icon.svg";
import RedXIcon from "../../assets/common/red_x_icon.svg";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface SlideOverProps {
    isOpen: boolean | undefined;
    onClose: () => void;
    children: React.ReactNode;
    showHeaderButtons?: boolean;
    onSave?: () => void;
    onDiscard?: () => void;
    discard?: boolean;
    saveDisabled?: boolean;
    padding?: string;
    bulkTasks?: boolean;
    closeOnRight?: boolean;
    hideCloseIcon?: boolean;
    headerBgColor?: string;
  }

export const SlideOverHeaderLeftContext = createContext<HTMLElement | null>(null);

export const SlideOverHeaderLeft = ({ children }: { children: React.ReactNode }) => {
  const slot = useContext(SlideOverHeaderLeftContext);
  if (!slot) return null;
  return createPortal(children, slot);
};

const SlideOver = ({ isOpen, onClose, children, 
  showHeaderButtons,
  onSave,
  onDiscard,
  discard = true,
  saveDisabled = false,
  padding,
  bulkTasks,
  closeOnRight = false,
  hideCloseIcon,
  headerBgColor = "#F4F0FF",
 }: SlideOverProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [headerLeftEl, setHeaderLeftEl] = useState<HTMLDivElement | null>(null);

  const isTaskSlideOver = Boolean(closeOnRight);
  const showHeaderPurple = isTaskSlideOver && Boolean(headerBgColor);

  // Only hide Close when this panel puts Save/Cancel on the right. Other
  // slideovers still show Close next to Save. Bulk create hides Cancel, so
  // keep Close available there.
  const shouldHideClose =
    hideCloseIcon ?? Boolean(showHeaderButtons && closeOnRight && !bulkTasks);

  useEffect(() => {
    if (isOpen) {
      // Wait for DOM + animation before resetting scroll
      setTimeout(() => {
        contentRef.current?.scrollTo(0, 0);
      }, 30);
    }
  }, [isOpen]);

  const closeControl = (
    <>
      <button
        className={`slide-over__close ${closeOnRight ? "nso-close-btn" : ""}`}
        onClick={onClose}
        style={closeOnRight ? { top: "14px" } : undefined}
      >
        {closeOnRight ? 
          <img src={RedXIcon} alt="Close" /> 
        :
          <img src={CloseIcon} alt="Close" /> 
        }
      </button>

      {!closeOnRight && (
        <h3 className="slide-over__close__text">Close</h3>
      )}
    </>
  );

  return (
    <>
      {isOpen && <div className="slide-over__overlay" onClick={onClose} />}
      <div className={`slide-over ${isOpen ? "slide-over--open" : ""} ${closeOnRight ? "slide-over--close-right" : ""}`}>
      <div className={`slide-over__header ${showHeaderPurple ? "slide-over__header--purple" : ""}`} style={showHeaderPurple && headerBgColor ? { backgroundColor: headerBgColor } : undefined}>
      <div className="slide-over__header-left" ref={setHeaderLeftEl}>
        {!closeOnRight && !shouldHideClose && closeControl}
        </div>
        <div className={`slide-over__header-right${closeOnRight && !shouldHideClose ? " slide-over__header-right--with-close" : ""}`}>
        {showHeaderButtons && (
            <>
              <button
                className={`btn btn-purple-compact ${saveDisabled ? "disabled-look" : ""}`}
                onClick={() =>{onSave && onSave()}}
              >
                Save
              </button>
              {!bulkTasks &&
              <button
                className="btn btn-outlined-small"
                onClick={onDiscard}
              >
                {discard ? "Discard" : "Cancel"}  
              </button>
              }
            </>
          )}
          {closeOnRight && !shouldHideClose && closeControl}
          </div>
        </div>
        <SlideOverHeaderLeftContext.Provider value={headerLeftEl}>
          <div ref={contentRef} className="slide-over__content" style={{padding: padding}}>{children}</div>
        </SlideOverHeaderLeftContext.Provider>
      </div>
    </>
  );
};

export default SlideOver;
